// src/data/deliveryAddresses.ts
// -----------------------------
// Repository for the `delivery_addresses` table (src/data/ layer, Part B). Its
// headline job is to OWN the soft-delete rule: registered-address reads must
// filter `WHERE NOT deactivated`, a rule duplicated across ~5 call sites today.
// Centralising it here means a caller can't forget it. Typed via getTypedClient()
// against the generated schema, so the queries are schema-checked.
//
// Keyed on shelter_id (the portal-owned surrogate). db-83 dropped the redundant
// delivery_addresses.shelter_code, so callers pass a shelter_id (shelters.id);
// writers set shelter_id on the row (no fill-trigger on this table any more).
//
// Functions log on error and return null (caller: null = couldn't load, leave
// state as-is). Rows are the generated delivery_addresses Row type.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type DeliveryAddressRow = Database["public"]["Tables"]["delivery_addresses"]["Row"];

// Active (non-deactivated) registered addresses for a shelter, default-first.
// The "WHERE NOT deactivated" rule lives here so call sites can't omit it.
export async function listActiveByShelterId(
  shelterId: string | null | undefined
): Promise<DeliveryAddressRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  if (!shelterId) return [];
  const { data, error } = await c
    .from("delivery_addresses")
    .select("*")
    .eq("shelter_id", shelterId)
    .eq("deactivated", false)
    .order("is_default", { ascending: false });
  if (error) {
    logger.warn("listActiveByShelterId failed:", error.message);
    return null;
  }
  return data ?? [];
}

// Distinct shelter ids that have at least one ACTIVE unverified delivery address
// (zephyr_verified = false, not deactivated) — drives the orange "pending
// verification" badge on the DFV Services list. Admin-scoped read (the "Staff
// manage all addresses" RLS policy lets staff/admin/superuser see all shelters).
// Returns null on error / no client.
export async function listShelterIdsWithPendingAddresses(): Promise<string[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("delivery_addresses")
    .select("shelter_id")
    .eq("zephyr_verified", false)
    .eq("deactivated", false)
    .limit(5000);
  if (error) {
    logger.warn("listShelterIdsWithPendingAddresses failed:", error.message);
    return null;
  }
  return Array.from(new Set((data ?? []).map((r) => r.shelter_id).filter(Boolean))) as string[];
}

// A single registered address by its id — used by the Sales Order builder to fill
// the structured delivery_* header columns from the request's chosen address. null
// on error / no client / not found / no id.
export async function getById(id: string | null | undefined): Promise<DeliveryAddressRow | null> {
  const c = getTypedClient();
  if (!c || !id) return null;
  const { data, error } = await c.from("delivery_addresses").select("*").eq("id", id).maybeSingle();
  if (error) {
    logger.warn("getById(delivery_addresses) failed:", error.message);
    return null;
  }
  return (data as DeliveryAddressRow) ?? null;
}

// ── Promotion model (db-146, docs/delivery-address-use-count-plan.md) ─────────
// An address is promoted onto the shelter's New Request selection list once it
// reaches this many responder verifications; at that point it is also flipped to
// zephyr_verified (trusted → no more approval holds). One number = cap = threshold.
export const ADDRESS_PROMOTION_THRESHOLD = 3;

// Pure: the next use_count after one verify (capped at the promotion threshold).
export function nextUseCount(current: number | null | undefined): number {
  return Math.min((Number(current) || 0) + 1, ADDRESS_PROMOTION_THRESHOLD);
}

// One verification step: read the address, bump use_count toward the threshold,
// and flip zephyr_verified true once it reaches it (the promotion point — the
// address then shows on the pick-list and is never held again). Returns the new
// { use_count, zephyr_verified } or null on error/not-found. Staff-only path (RLS
// "Staff manage all addresses" permits the write); the read-modify-write race
// between two responders is acceptable (low-frequency; worst case count off by 1).
export async function verifyAddressStep(
  id: string | null | undefined
): Promise<{ use_count: number; zephyr_verified: boolean } | null> {
  if (!id) return null;
  const row = await getById(id);
  if (!row) return null;
  const use_count = nextUseCount(row.use_count as number | null | undefined);
  const zephyr_verified = use_count >= ADDRESS_PROMOTION_THRESHOLD;
  const { error } = await updateAddressById(id, { use_count, zephyr_verified });
  if (error) return null;
  return { use_count, zephyr_verified };
}

// ── Admin management (Shelters & Users → Manage Addresses) ───────────────────
// The admin list deliberately includes deactivated rows (they can be reactivated),
// so it does NOT apply the deactivated filter. Writes take loose row objects (the
// admin form builds plain objects) and are cast to the generated Insert/Update
// shapes internally — aligning the form shape to the schema is a later enrichment.

type DeliveryAddressInsert = Database["public"]["Tables"]["delivery_addresses"]["Insert"];
type DeliveryAddressPatch = Database["public"]["Tables"]["delivery_addresses"]["Update"];
// Loose object input (the admin form builds interface-typed objects, which aren't
// assignable to Record<string, unknown>); cast to the generated shapes internally.
type AddrInput = object;
type WriteResult = { error: { message: string } | null };

// ALL addresses for a shelter (incl. deactivated) — admin management view.
export async function listAllByShelterId(
  shelterId: string | null | undefined
): Promise<DeliveryAddressRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  if (!shelterId) return [];
  const { data, error } = await c
    .from("delivery_addresses")
    .select("*")
    .eq("shelter_id", shelterId)
    .order("is_default", { ascending: false });
  if (error) {
    logger.warn("listAllByShelterId failed:", error.message);
    return null;
  }
  return data ?? [];
}

// ALL addresses for MANY shelters at once (incl. deactivated — a re-import must
// not resurrect a deliberately deactivated address, so the dedupe baseline sees
// them too). Used by the Unleashed Delivery-Address import. Chunked .in() (100
// ids per query) so a full import (260+ shelters) neither builds an over-long
// URL nor trips PostgREST's silent 1,000-row default cap (explicit .limit per
// chunk — the suppliers lesson, 2 July 2026).
export async function listAddressesByShelterIds(
  shelterIds: string[]
): Promise<DeliveryAddressRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const out: DeliveryAddressRow[] = [];
  for (let i = 0; i < shelterIds.length; i += 100) {
    const chunk = shelterIds.slice(i, i + 100);
    const { data, error } = await c
      .from("delivery_addresses")
      .select("*")
      .in("shelter_id", chunk)
      .limit(5000);
    if (error) {
      logger.warn("listAddressesByShelterIds failed:", error.message);
      return null;
    }
    out.push(...(data ?? []));
  }
  return out;
}

export async function insertAddresses(rows: AddrInput[]): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("delivery_addresses")
    .insert(rows as unknown as DeliveryAddressInsert[]);
  if (error) logger.warn("insertAddresses failed:", error.message);
  return { error };
}

// Insert ONE address row and return its new id — used by the verified-address
// one-off capture (a request must link to the created row via delivery_address_id).
export async function insertAddressReturning(
  row: AddrInput
): Promise<{ data: { id: string } | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("delivery_addresses")
    .insert(row as unknown as DeliveryAddressInsert)
    .select("id")
    .single();
  if (error) logger.warn("insertAddressReturning failed:", error.message);
  return { data: (data as { id: string } | null) ?? null, error };
}

export async function updateAddressById(id: string, patch: AddrInput): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("delivery_addresses")
    .update(patch as DeliveryAddressPatch)
    .eq("id", id);
  if (error) logger.warn("updateAddressById failed:", error.message);
  return { error };
}

export async function clearDefaultForShelterId(
  shelterId: string | null | undefined
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  if (!shelterId) return { error: null };
  const { error } = await c
    .from("delivery_addresses")
    .update({ is_default: false } as DeliveryAddressPatch)
    .eq("shelter_id", shelterId);
  if (error) logger.warn("clearDefaultForShelterId failed:", error.message);
  return { error };
}

// NB: there is deliberately NO generic upsertAddresses(rows, onConflict) here. Its
// only caller (the Step 1 Customers import) targeted ON CONFLICT (shelter_id,
// address_name) — an index db-129 DROPPED — so every call raised "no unique or
// exclusion constraint matching" and, with the error discarded, the import silently
// created no address (finding A2, fixed 22 Aug 2026). This table has no natural
// unique key to upsert on; dedupe addresses by CONTENT via
// planDefaultDeliveryAddress / planDeliveryImport in src/services/deliveryImport.ts,
// then insertAddresses().
